/**
 * Copyright(c) 2023 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Naseef O
 */
"use server";

import {
  ProductDetailsDocument,
  GetCategoryBySlugDocument,
  SearchBrandsDocument,
  SearchBrandsQuery,
  GetOrderDetailsQuery,
  GetCollectionBySlugDocument,
  GetCategoryBySlugQuery,
  GetCollectionBySlugQuery,
  GetBrandBySlugDocument,
  GetBrandBySlugQuery,
  GetOrderDetailsDocument,
  LensConfigListDocument,
  CustomerPrescriptionsDocument,
  CreatePrescriptionDocument,
  SelectPrescriptionDocument,
  LensConfigListQuery,
  CreatePrescriptionMutation,
  SelectPrescriptionMutation,
  CustomerPrescriptionsQuery,
  ProductSeoDetailsQuery,
  ProductSeoDetailsDocument,
  UserDetailsDocument,
  DeletePrescriptionMutation,
  DeletePrescriptionDocument,
  UpdatePrescriptionMutation,
  UpdatePrescriptionDocument,
  BrandSortingInput,
  GetAllBrandsQuery,
  GetAllBrandsDocument,
  LanguageCodeEnum,
  UserDetailsQuery,
  GetProductVariantDocument,
  GetProductVariantQuery,
  CustomerNoteAddMutation,
  CustomerNoteAddDocument,
  GetCollectionByIdQuery,
  GetCollectionByIdDocument,
  GetLiveVariantDetailsDocument,
  GetLiveVariantDetailsQuery,
  GetCollectionListBySlugsDocument,
  CollectionFilterInput,
  GetCollectionListBySlugsQuery,
  PrescriptionImageDeleteMutation,
  PrescriptionImageDeleteDocument,
  GetVariantByIdQuery,
  GetVariantByIdDocument,
  ClipOnDetailsDocument,
  GetVariantsBySkusDocument,
  GetVariantsBySkusQuery,
} from "../generated/graphql";
import SaleorClient from "./SaleorClient";
import PublicSaleorClient from "./PublicSaleorClient";
import {
  BrandFilterInput,
  Brands,
  CollectionList,
  CreatedPrescription,
  CustomerNoteResponse,
  CustomerPrescriptions,
  LensConfigs,
  LiveVariantDetails,
  Order,
  Product,
  ProductEdges,
  ProductListingPageData,
  ProductSeo,
  UserDetails,
  VariantItemPdp,
} from "../../../common/lib/types";
import { webHookToken } from "../../../runtimeConfig/server";
import { olRevalidateTime, revalidateTime } from "../../../runtimeConfig/public";
import { prescriptionSources } from "../../../apiUtils/constants";
import { inspect } from "util";
import { getServerSession } from "../../../apiUtils/authServerUtils";

const revalidate = revalidateTime;

export const getUserDetails = async (channel: string): Promise<UserDetails> => {
  // console.log("calling getUserDetails", {
  //   query: UserDetailsDocument.toString()
  // })

  // console.log("at "+Date.now());

  const saleorClient = new SaleorClient();
  const response: { data: UserDetailsQuery } = await saleorClient.post(
    UserDetailsDocument.toString(),
    { channel },
    "",
    {
      revalidate: 4,
    },
  );

  console.log("UserDetailsResponse ", response);
  return (response?.data?.me as unknown as UserDetails) || null;
};

export const createAccountAddress = async (channel: string): Promise<Product> => {
  const saleorClient = new SaleorClient();
  const response: { data: any } = await saleorClient.post(
    UserDetailsDocument.toString(),
    { channel },
    "",
    {
      revalidate: 4,
    },
  );

  return response?.data?.me || null;
};

export const getPDPVariantById = async (id: string, channel: string): Promise<VariantItemPdp> => {
  const saleorClient = new PublicSaleorClient();
  const session = await getServerSession();
  const includePromotionPreview = !!session?.accessToken;

  if (session?.accessToken) {
    await saleorClient.init(undefined, session.accessToken);
  }

  const response: { data: GetVariantByIdQuery } = await saleorClient.post(
    GetVariantByIdDocument.toString(),
    { id, channel, includePromotionPreview },
    "",
    {
      revalidate: includePromotionPreview ? 0 : revalidate,
    },
  );
  return (response?.data?.productVariant as unknown as VariantItemPdp) || null;
};

export const getProductDetails = async (slug: string, channel: string): Promise<Product> => {
  const saleorClient = new PublicSaleorClient();
  const session = await getServerSession();
  const includePromotionPreview = !!session?.accessToken;

  if (session?.accessToken) {
    await saleorClient.init(undefined, session.accessToken);
  } else {
    saleorClient.init();
  }

  // TODO: Add Proper type
  const response: { data: any } = await saleorClient.post(
    ProductDetailsDocument.toString(),
    { slug, channel, includePromotionPreview },
    "",
    {
      revalidate: includePromotionPreview ? 0 : revalidate,
    },
  );

  return (response?.data?.product as Product) || null;
};

export const getProductVariant = async (sku: string, channel: string) => {
  const saleorClient = new SaleorClient();

  const response: { data: GetProductVariantQuery } = await saleorClient.post(
    GetProductVariantDocument.toString(),
    { sku, channel },
    "",
    {
      revalidate,
    },
  );
  return response?.data?.productVariant || null;
};

export const getVariantsBySkus = async (
  skus: string[],
  channel: string,
  first: number = 100,
) => {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetVariantsBySkusQuery } = await saleorClient.post(
    GetVariantsBySkusDocument.toString(),
    { skus, channel, first },
    "",
    {
      revalidate,
    },
  );
  return response?.data?.productVariants?.edges?.map((edge) => edge.node) || [];
};

export async function getCategoryBySlug(
  slug: string,
  languageCode: LanguageCodeEnum = LanguageCodeEnum.En,
): Promise<ProductListingPageData | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetCategoryBySlugQuery } = await saleorClient.post(
    GetCategoryBySlugDocument.toString(),
    { slug, languageCode },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.category as ProductListingPageData) || null;
}

export async function getCollectionBySlug(
  slug: string,
  channel: string,
): Promise<ProductListingPageData | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetCollectionBySlugQuery } = await saleorClient.post(
    GetCollectionBySlugDocument.toString(),
    { slug, channel },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.collection as ProductListingPageData) || null;
}

export async function getCollectionListBySlugs(
  filter: CollectionFilterInput,
  first: number,
  channel: string,
): Promise<CollectionList | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetCollectionListBySlugsQuery } = await saleorClient.post(
    GetCollectionListBySlugsDocument.toString(),
    { filter, first, channel },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.collections as CollectionList) || null;
}

export async function getCollectionById(
  id: string,
  channel: string,
): Promise<{ slug: string; url: string } | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetCollectionByIdQuery } = await saleorClient.post(
    GetCollectionByIdDocument.toString(),
    { id, channel },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.collection as { slug: string; url: string }) || null;
}

export async function getBrandBySlug(
  slug: string,
  languageCode: LanguageCodeEnum = LanguageCodeEnum.En,
): Promise<ProductListingPageData | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetBrandBySlugQuery } = await saleorClient.post(
    GetBrandBySlugDocument.toString(),
    { slug, languageCode },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.brand as ProductListingPageData) || null;
}

export async function getAllBrands(
  first: number = 10,
  filter: BrandFilterInput,
): Promise<Brands | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: SearchBrandsQuery } = await saleorClient.post(
    `${SearchBrandsDocument}`,
    { first, filter },
    "",
    {
      revalidate,
    },
  );
  return (response?.data?.brands as Brands) || null;
}

export async function getAllSortedBrands(
  first: number = 100,
  sort: BrandSortingInput,
  channel: string,
  locale: LanguageCodeEnum,
): Promise<Brands | null> {
  const saleorClient = new PublicSaleorClient();
  saleorClient.init();

  const response: { data: GetAllBrandsQuery } = await saleorClient.post(
    `${GetAllBrandsDocument}`,
    { first, sort, filter: {}, channel, locale },
    "",
    {
      revalidate,
    },
  );
  return (response?.data?.brands as Brands) || null;
}

/**
 * This method is used to get order details
 *
 * @export
 * @param {string} id
 * @return {*}  {Promise<Order>}
 */
export async function getOrderDetails(id: string): Promise<Order> {
  const saleorClient = new SaleorClient();

  const response: { data: GetOrderDetailsQuery } = await saleorClient.post(
    `${GetOrderDetailsDocument}`,
    { id },
    "",
    {
      revalidate: 4,
    },
  );
  return (response?.data?.order as Order) || null;
}

export async function getLensConfigs(
  channel: string,
  filters: Record<string, any>,
  first: number = 20,
): Promise<LensConfigs> {
  const saleorClient = new SaleorClient();

  const response: { data: LensConfigListQuery } = await saleorClient.post(
    `${LensConfigListDocument}`,
    {
      first,
      channel,
      filter: { ...filters },
      includeCollections: false,
    },
    "",
    {
      revalidate: olRevalidateTime,
      tags: [`${channel}-ol-variants`],
    },
  );

  return (response?.data?.lensConfigurators as LensConfigs) || null;
}

export async function getPrescriptions(
  first?: number,
  after?: string,
  last?: number,
  before?: string,
): Promise<CustomerPrescriptions> {
  const saleorClient = new SaleorClient();

  console.log("Get Prescriptions Payload", {
    first: first || 20,
    after,
    last,
    before,
  });

  const response: { data: CustomerPrescriptionsQuery } = await saleorClient.post(
    `${CustomerPrescriptionsDocument}`,
    {
      first: first || 20,
      after,
      last,
      before,
    },
    "no-store",
  );

  console.log("Get Prescriptions Response", inspect(response, false, null, true));
  return (response?.data?.customerPrescriptions as CustomerPrescriptions) || null;
}

export async function uploadPrescription(
  formData: FormData,
  title: string,
): Promise<CreatedPrescription> {
  const saleorClient = new SaleorClient();

  const response: { data: CreatePrescriptionMutation } = await saleorClient.postForm(
    `${CreatePrescriptionDocument}`,
    {
      title,
      source: prescriptionSources.USER_UPLOADED,
      alt: "",
      image1: null,
      image2: null,
    },
    formData,
    "createPrescription",
  );
  return (response.data.customerPrescriptionCreate as CreatedPrescription) || null;
}

export async function updatePrescription(
  id: string,
  formData: FormData,
  title: string,
): Promise<UpdatePrescriptionMutation> {
  const saleorClient = new SaleorClient();

  const response: { data: UpdatePrescriptionMutation } = await saleorClient.postForm(
    `${UpdatePrescriptionDocument}`,
    {
      id,
      title,
      source: prescriptionSources.USER_UPLOADED,
      alt: "",
      image1: null,
      image2: null,
    },
    formData,
    "updatePrescription",
  );
  return response?.data;
}

export async function deletePrescriptionImage(
  id: string,
): Promise<PrescriptionImageDeleteMutation> {
  const saleorClient = new SaleorClient();

  const response: { data: PrescriptionImageDeleteMutation } = await saleorClient.post(
    `${PrescriptionImageDeleteDocument}`,
    {
      id,
    },
    "no-store",
  );
  return response?.data;
}

export async function selectPrescription(userId: string, prescriptionId: string): Promise<any> {
  const saleorClient = new SaleorClient();

  const response: { data: SelectPrescriptionMutation } = await saleorClient.post(
    `${SelectPrescriptionDocument}`,
    {
      userId,
      prescriptionId,
    },
    "no-store",
  );
  return response;
}

export async function deletePrescription(id: string): Promise<any> {
  const saleorClient = new SaleorClient();

  const response: { data: DeletePrescriptionMutation } = await saleorClient.post(
    `${DeletePrescriptionDocument}`,
    {
      id,
    },
    "no-store",
  );
  return response;
}

/**
 * This method is used to fetch the product specific seo details
 *
 * @param slug
 * @param channel
 * @returns
 */
export async function getProductSeo(slug: string, channel: string): Promise<ProductSeo> {
  const saleorClient = new SaleorClient();

  const response: { data: ProductSeoDetailsQuery } = await saleorClient.post(
    ProductSeoDetailsDocument.toString(),
    { slug, channel },
    "",
    {
      revalidate,
    },
  );
  return (response?.data?.product as ProductSeo) || null;
}

export async function updateOrderLine(
  orderId: string,
  customerNote: string,
): Promise<CustomerNoteResponse> {
  const saleorClient = new SaleorClient();

  const response: { data: CustomerNoteAddMutation } = await saleorClient.postWithToken(
    webHookToken,
    `${CustomerNoteAddDocument}`,
    {
      id: orderId,
      customerNote,
    },
    "no-store",
  );
  return (response?.data?.customerNoteAdd as CustomerNoteResponse) || null;
}

export const getLiveVariantDetails = async (
  id: string,
  channel: string,
): Promise<LiveVariantDetails> => {
  const saleorClient = new SaleorClient();
  const response: { data: GetLiveVariantDetailsQuery } = await saleorClient.post(
    GetLiveVariantDetailsDocument.toString(),
    { id, channel },
    "no-store",
  );
  return (response?.data?.productVariant as unknown as LiveVariantDetails) || null;
};
export async function getTopsBaseProducts(
  brands: string[],
  categories: string[],
  channel: string
): Promise<ProductEdges> {
  const saleorClient = new SaleorClient();
  const response: { data: { products: { edges: ProductEdges } }; errors: unknown } = await saleorClient.post(
    ClipOnDetailsDocument.toString(),
    { brands, categories, channel },
    "",
    {
      revalidate,
    },
  );

  return (response?.data?.products?.edges as ProductEdges) || null;
}